import { createFileRoute } from '@tanstack/react-router';
import { ListChecks, Lock } from 'lucide-react';
import { ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { DueReportRow } from '../components/teacher-reports/DueReportRow';
import { isReportsDisabled, useDueReports } from '../components/teacher-reports/useDueReports';
import { ReportHistory } from '../components/teacher-reports/ReportHistory';

export const Route = createFileRoute('/_authed/me/reports')({
  component: MyReportsPage,
});

function MyReportsPage() {
  const user = useAuthStore((s) => s.user);
  // Shown to staff who open this teacher page: point them to their own one.
  const reviewPath = useMenuPath('/academics/report-review');
  if (user && user.primaryRole !== 'teacher') {
    return (
      <ListShell title="My reports">
        <EmptyState
          title="Reports are submitted by teachers"
          description={reviewPath ? `You review teachers’ reports in ${reviewPath}.` : 'Nalims and admins review teachers’ reports.'}
          icon={<ListChecks size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <TeacherReports />;
}

function TeacherReports() {
  const due = useDueReports();
  const now = new Date();

  return (
    <ListShell
      title="My reports"
      description="Daily, weekly and monthly reports open automatically from your class timetable — each one as soon as the class, week or month is done."
    >
      {due.isLoading ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : due.isError ? (
        isReportsDisabled(due.error) ? (
          <EmptyState
            title="Reports aren’t switched on for you yet"
            description="Your academy is rolling reports out in stages. They’ll appear here as soon as they’re on for you."
            icon={<ListChecks size={32} aria-hidden="true" />}
          />
        ) : (
          <ErrorState
            title="Could not load your reports"
            description={due.error instanceof ApiError ? due.error.message : undefined}
            onRetry={() => void due.refetch()}
          />
        )
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border border-gold-500/60 bg-gradient-to-b from-gold-50/50 to-[color:var(--color-surface)] px-4 py-3 dark:from-gold-700/15 dark:to-[color:var(--color-surface)]">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <ListChecks size={16} aria-hidden="true" /> Open now
              <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[color:var(--color-surface-strong)] px-1.5 text-[10px] font-semibold tabular-nums">
                {due.data?.open.length ?? 0}
              </span>
            </h2>
            {(due.data?.open.length ?? 0) > 0 ? (
              <ul className="mt-1">
                {due.data!.open.map((r) => (
                  <DueReportRow key={`${r.kind}-${r.batchId}-${r.periodKey}`} report={r} now={now} />
                ))}
              </ul>
            ) : (
              <p className="py-2 text-sm text-[color:var(--color-text-muted)]">Nothing open right now — alhamdulillah.</p>
            )}
          </section>
          {(due.data?.upcoming.length ?? 0) > 0 ? (
            <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Lock size={14} aria-hidden="true" /> Coming up
              </h2>
              <ul className="mt-1">
                {due.data!.upcoming.map((r) => (
                  <DueReportRow key={`${r.kind}-${r.batchId}-${r.periodKey}`} report={r} now={now} />
                ))}
              </ul>
            </section>
          ) : null}
          {(due.data?.recent.length ?? 0) > 0 ? (
            <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3">
              <h2 className="text-sm font-semibold">Submitted this week</h2>
              <ul className="mt-1">
                {due.data!.recent.map((r) => (
                  <DueReportRow key={`${r.kind}-${r.batchId}-${r.periodKey}`} report={r} now={now} />
                ))}
              </ul>
            </section>
          ) : null}
          <ReportHistory />
        </div>
      )}
    </ListShell>
  );
}
